import { formatMoney, formatPlate, t } from '@aquabox/shared';
import { useLiveTrips, useVehiclePositions } from '../../api/queries';
import type { Trip, TripStatus, VehiclePosition } from '../../api/types';

/**
 * Açık seferlerin mobil adım karşılığı (`apps/mobile/lib/features/driver_trip/steps/step_resolver.dart`):
 * OPENED ve LOADING mobilde "Yükle" (3), ON_ROUTE "Yolda" (4), CLOSING "Kapanış" (6).
 * Mobilde OPENED için ayrı bir adım yoktur; "Sefere çık" (2) yalnız sefer henüz açık değilken görünür.
 */
const DRIVER_STEP_OF: Partial<Record<TripStatus, number>> = {
  OPENED: 3,
  LOADING: 3,
  ON_ROUTE: 4,
  CLOSING: 6,
};
const DRIVER_STEP_TOTAL = 7;

/** `Trips.tsx` STATUS_COLOR ile aynı renkler (dosyalar arası döngüsel import olmasın diye kopya). */
const LIVE_STATUS_COLOR: Partial<Record<TripStatus, string>> = {
  OPENED: 'var(--info)',
  LOADING: 'var(--warning)',
  ON_ROUTE: 'var(--primary)',
  CLOSING: 'var(--warning)',
};

/** Kart gösterilecek sefer: açık (adım eşlemesi olan) ve iptal edilmemiş. */
function isLive(trip: Trip): boolean {
  return trip.voidedAt === null && DRIVER_STEP_OF[trip.status] !== undefined;
}

/**
 * Son görülme metni. Konum alanı `GET /api/tracking/positions` üzerinden gelir (araç başına son
 * `LocationPing`); `stale` 3 dakikadan eski ya da hiç yoksa "sinyal yok". Konum sorgusu
 * HATA verdiyse sinyal yok denemez: bilinmiyor, "Konum alınamadı".
 */
function lastSeenText(position: VehiclePosition | undefined, failed: boolean): string {
  if (failed) return t('trips.live.locationUnavailable');
  if (!position || position.ageSeconds === null) return t('map.noSignal');
  if (position.stale) return t('map.noSignalSince', { n: Math.round(position.ageSeconds / 60) });
  return t('map.lastSeen', { n: position.ageSeconds });
}

/**
 * Seferler ekranının üstündeki açık sefer kartları (§3.9). Sayılar sunucudan gelir; panel
 * yalnız gösterir (kural 4). Açık seferde fark uyarısı YOKTUR (fark kapanışta doğar); kart
 * "araçta olmalı" rakamını gösterir.
 */
export function LiveTripCards() {
  const live = useLiveTrips();
  const positions = useVehiclePositions();

  const positionByVehicle = new Map((positions.data ?? []).map((p) => [p.vehicle.id, p]));
  const trips = (live.data?.items ?? []).filter(isLive);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <strong style={{ fontSize: 15 }}>{t('trips.live.title')}</strong>
      {live.isPending ? (
        <p style={{ color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
      ) : live.isError ? (
        <p style={{ color: 'var(--danger)' }}>{t('error.internal')}</p>
      ) : trips.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)', fontSize: 13.5 }}>{t('trips.live.empty')}</p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 14,
          }}
        >
          {trips.map((trip) => (
            <LiveTripCard
              key={trip.id}
              trip={trip}
              lastSeen={lastSeenText(positionByVehicle.get(trip.vehicle.id), positions.isError)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function LiveTripCard({ trip, lastSeen }: { trip: Trip; lastSeen: string }) {
  const step = DRIVER_STEP_OF[trip.status] ?? 0;

  return (
    <article
      data-testid={`live-trip-${trip.id}`}
      className="card"
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <strong style={{ fontSize: 16 }}>{formatPlate(trip.vehicle.plate)}</strong>
          <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
            {trip.driver.user.fullName}
          </div>
        </div>
        <span
          className="pill"
          style={{
            background: LIVE_STATUS_COLOR[trip.status] ?? 'var(--text-secondary)',
            color: '#fff',
            fontSize: 11.5,
            fontWeight: 700,
            alignSelf: 'flex-start',
          }}
        >
          {t(`tripStatus.${trip.status}`)}
        </span>
      </div>

      <div style={{ fontSize: 13, fontWeight: 600 }}>
        {t('trips.live.step', { n: step, total: DRIVER_STEP_TOTAL })}
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13.5 }}>
        <span>{t('trips.live.delivered', { n: trip.deliveryCount })}</span>
        <span>{t('trips.live.remaining', { n: trip.openOrderCount ?? '—' })}</span>
      </div>

      <div style={{ fontSize: 13.5 }}>
        {t('trips.live.collected')}: <strong>{formatMoney(trip.collected.total)}</strong>
      </div>

      {trip.onVehicle ? (
        <div data-testid="on-vehicle" style={{ fontSize: 13.5 }}>
          {t('trips.live.onVehicle', { n: trip.onVehicle.expectedFull })}
          {trip.onVehicle.pendingReloadFull !== 0 ? (
            <div
              data-testid="pending-reload"
              style={{ fontSize: 12, color: 'var(--text-secondary)' }}
            >
              {t('trips.live.pendingReload', {
                n: `${trip.onVehicle.pendingReloadFull > 0 ? '+' : ''}${trip.onVehicle.pendingReloadFull}`,
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{lastSeen}</div>
    </article>
  );
}

/**
 * Kapanmış seferler için denklem özeti (tablonun altında). Yalnız `CLOSED` ve `applicable`
 * seferler sayılır (açık ve iptal sayılmaz); "toplam mutlak fark" satır başına
 * `diffFull` / `diffEmpty` mutlak değerlerinin toplamıdır (iki sefer +2 / −2 ise 4, 0 değil) (adet, para değil). Liste sayfası sınırlıdır (100); özet bu listeyi anlatır.
 */
export function ClosedEquationSummary({ trips }: { trips: Trip[] }) {
  const closed = trips.filter(
    (trip) => trip.status === 'CLOSED' && trip.voidedAt === null && trip.equation.applicable,
  );
  if (closed.length === 0) return null;

  let full = 0;
  let empty = 0;
  for (const trip of closed) {
    for (const line of trip.equation.lines) {
      // Koli damacana değildir; dolu/boş toplamına girmez (kapanış denklemindeki ayrım).
      if (line.kind === 'CASE_05L') continue;
      full += Math.abs(line.diffFull);
      empty += Math.abs(line.diffEmpty);
    }
  }

  return (
    <div
      data-testid="trips-closed-summary"
      style={{ padding: '12px 22px', borderTop: '1px solid var(--border)', fontSize: 13.5 }}
    >
      {t('trips.live.closedSummary', { n: closed.length, full, empty })}
    </div>
  );
}
